import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {toast} from 'react-toastify';
import Dialog from '@material-ui/core/Dialog';
import {DialogField, FieldLabel, FieldInput} from 'routes/App/dashboard/styles';
import Loading from '../../../../../components/shared/Utils/Loading';
import DialogContainer from '../../../../../components/DialogContainer/styles';
import DialogClose from '../../../../../components/DialogClose/styles';
import DialogTitle from '../../../../../components/DialogTitle/styles';
import DialogBody from '../../../../../components/DialogBody/styles';
import DialogActions from '../../../../../components/DialogActions/styles';
import ButtonPrimary from '../../../../../components/buttons/ButtonPrimary/styles';
import ButtonOutline from '../../../../../components/buttons/ButtonOutline/styles';
import {
  getPlatformCommissions,
  updatePlatformCommissions
} from '../../../../../helpers/services/api/hubspot';
import {
  PlatformCommissionsContainer,
  PlatformSection,
  PlatformTitle,
  CommissionField,
  CommissionInputWrapper,
  ErrorMessage
} from './styles';

const PLATFORM_NAMES = {
  GH: 'GrubHub',
  UE: 'UberEats',
  DD: 'DoorDash',
  Slice: 'Slice'
};

const PLATFORM_ORDER = {UE: 1, DD: 2, GH: 3, Slice: 4};

const getValidationErrors = (commissionsToValidate) => {
  const newErrors = {};

  commissionsToValidate.forEach((commission) => {
    const {
      platform,
      platformCommissionSelfDelivery,
      platformCommissionPlatformDelivery
    } = commission;

    // Check if marketplace commission is mandatory and > 0
    if (
      !platformCommissionPlatformDelivery ||
      parseFloat(platformCommissionPlatformDelivery) <= 0
    ) {
      newErrors[`${platform}_platformCommissionPlatformDelivery`] =
        'Mandatory field';
    }

    if (
      (platformCommissionSelfDelivery !== 0 &&
        platformCommissionSelfDelivery !== '0' &&
        !platformCommissionSelfDelivery) ||
      parseFloat(platformCommissionSelfDelivery) < 0
    ) {
      newErrors[`${platform}_platformCommissionSelfDelivery`] =
        'Mandatory field';
    }

    // Check if self delivery is lower than marketplace
    if (
      platformCommissionPlatformDelivery &&
      platformCommissionSelfDelivery &&
      parseFloat(platformCommissionSelfDelivery) >=
        parseFloat(platformCommissionPlatformDelivery)
    ) {
      newErrors[`${platform}_platformCommissionSelfDelivery`] =
        'Value should be lower than Marketplace';
    }
  });

  return newErrors;
};

const PlatformCommissionsSettingsModal = ({
  open,
  onClose,
  locationId,
  initialCommissions,
  onSaveSuccess
}) => {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const loadCommissions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getPlatformCommissions(locationId);
      if (response?.data) {
        // Transform snake_case API response to camelCase for frontend
        const transformedData = response.data.map((item) => ({
          platform: item.platform,
          platformCommissionSelfDelivery: item.platformCommissionSelfDelivery,
          platformCommissionPlatformDelivery:
            item.platformCommissionPlatformDelivery
        }));
        setCommissions(transformedData);
        setErrors({});
        setTouched({});
      }
    } catch (error) {
      console.error('Error loading platform commissions:', error);
      toast.error('Failed to load platform commissions');
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    if (open && locationId) {
      if (initialCommissions != null) {
        setCommissions(initialCommissions);
        setErrors({});
        setTouched({});
      } else {
        loadCommissions();
      }
    }
  }, [open, locationId, loadCommissions, initialCommissions]);

  const handleInputChange = (platform, field, value) => {
    setCommissions((prev) => {
      const updated = prev.map((item) =>
        item.platform === platform ? {...item, [field]: value} : item
      );

      const newErrors = getValidationErrors(updated);
      setErrors((prevErrors) => {
        const cleared = {...prevErrors};
        Object.keys(cleared).forEach((key) => {
          if (key.startsWith(`${platform}_`) && !newErrors[key]) {
            delete cleared[key];
          }
        });
        Object.keys(newErrors).forEach((key) => {
          if (key.startsWith(`${platform}_`)) {
            cleared[key] = newErrors[key];
          }
        });
        return cleared;
      });

      return updated;
    });

    setTouched((prev) => ({
      ...prev,
      [`${platform}_${field}`]: true
    }));
  };

  const validateForm = () => {
    const newErrors = getValidationErrors(commissions);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const transformedCommissions = commissions.map((item) => ({
        platform: item.platform,
        platformName: PLATFORM_NAMES[item.platform] || item.platform,
        platformCommissionSelfDelivery: item.platformCommissionSelfDelivery,
        platformCommissionPlatformDelivery:
          item.platformCommissionPlatformDelivery
      }));
      const response = await updatePlatformCommissions(
        locationId,
        transformedCommissions
      );
      if (response?.status === 200 || response?.status === 204) {
        toast.success('Platform commissions updated successfully');
        if (typeof onSaveSuccess === 'function') {
          onSaveSuccess(commissions);
        }
        onClose();
      }
    } catch (error) {
      console.error('Error updating platform commissions:', error);
      toast.error('Failed to update platform commissions');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    loadCommissions(); // Reset to original values
    setErrors({});
    setTouched({});
    onClose();
  };

  const visibleCommissions = commissions.sort(
    (a, b) =>
      (PLATFORM_ORDER[a.platform] || 99) - (PLATFORM_ORDER[b.platform] || 99)
  );

  return (
    <Dialog open={open} onClose={handleCancel} scroll='body'>
      <DialogContainer className='DialogMd'>
        <DialogClose onClick={handleCancel} />
        <DialogTitle className='marginBottomLg'>Settings</DialogTitle>
        <DialogBody>
          <PlatformCommissionsContainer>
            {(() => {
              if (loading && !commissions.length) {
                return <div>Loading...</div>;
              }
              if (visibleCommissions.length === 0) {
                return (
                  <div
                    style={{
                      padding: '20px',
                      textAlign: 'center',
                      color: '#929cb9'
                    }}
                  >
                    No platforms found
                  </div>
                );
              }
              return visibleCommissions.map((commission) => {
                const {
                  platform,
                  platformCommissionSelfDelivery,
                  platformCommissionPlatformDelivery
                } = commission;
                const platformName = PLATFORM_NAMES[platform] || platform;

                return (
                  <PlatformSection key={platform}>
                    <PlatformTitle>{platformName}</PlatformTitle>
                    <div
                      style={{
                        width: '100%',
                        height: '1px',
                        backgroundColor: 'rgba(47, 61, 72, 0.1)',
                        marginBottom: '16px'
                      }}
                    />

                    <div>
                      <CommissionField style={{marginBottom: 0}}>
                        <FieldLabel
                          className='FieldLabelLg'
                          style={{marginBottom: 0, whiteSpace: 'nowrap'}}
                        >
                          <span style={{color: '#ff2d38'}}>*</span> Marketplace
                          delivery commission
                        </FieldLabel>
                        <CommissionInputWrapper>
                          <FieldInput
                            type='number'
                            step='0.1'
                            min='0'
                            value={platformCommissionPlatformDelivery || ''}
                            onChange={(e) =>
                              handleInputChange(
                                platform,
                                'platformCommissionPlatformDelivery',
                                e.target.value
                              )
                            }
                            className={`FieldNumber ${
                              errors[
                                `${platform}_platformCommissionPlatformDelivery`
                              ]
                                ? 'error'
                                : ''
                            }`}
                            placeholder='-'
                            style={{width: '110px', margin: '2px'}}
                          />
                          <span style={{marginLeft: '8px', color: '#2f3d48'}}>
                            %
                          </span>
                        </CommissionInputWrapper>
                      </CommissionField>
                      {errors[
                        `${platform}_platformCommissionPlatformDelivery`
                      ] && (
                        <ErrorMessage>
                          {
                            errors[
                              `${platform}_platformCommissionPlatformDelivery`
                            ]
                          }
                        </ErrorMessage>
                      )}
                    </div>

                    <div>
                      <CommissionField>
                        <FieldLabel
                          className='FieldLabelLg'
                          style={{marginBottom: 0, whiteSpace: 'nowrap'}}
                        >
                          <span style={{color: '#ff2d38'}}>*</span> Self
                          Delivery commission
                        </FieldLabel>
                        <CommissionInputWrapper>
                          <FieldInput
                            type='number'
                            step='0.1'
                            min='0'
                            value={platformCommissionSelfDelivery ?? ''}
                            onChange={(e) =>
                              handleInputChange(
                                platform,
                                'platformCommissionSelfDelivery',
                                e.target.value
                              )
                            }
                            className={`FieldNumber ${
                              errors[
                                `${platform}_platformCommissionSelfDelivery`
                              ]
                                ? 'error'
                                : ''
                            }`}
                            placeholder='-'
                            style={{width: '110px', margin: '2px'}}
                          />
                          <span style={{marginLeft: '8px', color: '#2f3d48'}}>
                            %
                          </span>
                        </CommissionInputWrapper>
                      </CommissionField>
                      {errors[`${platform}_platformCommissionSelfDelivery`] && (
                        <ErrorMessage>
                          {errors[`${platform}_platformCommissionSelfDelivery`]}
                        </ErrorMessage>
                      )}
                    </div>
                  </PlatformSection>
                );
              });
            })()}
          </PlatformCommissionsContainer>
        </DialogBody>
        <DialogActions className='justifyEnd mTop32'>
          <ButtonOutline onClick={handleCancel} style={{marginRight: '16px'}}>
            Cancel
          </ButtonOutline>
          {loading ? (
            <Loading className='LoadingExportButton' />
          ) : (
            <ButtonPrimary
              onClick={handleSave}
              disabled={Object.keys(errors).length > 0}
            >
              <span>Save</span>
            </ButtonPrimary>
          )}
        </DialogActions>
      </DialogContainer>
    </Dialog>
  );
};

PlatformCommissionsSettingsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  locationId: PropTypes.string.isRequired,
  initialCommissions: PropTypes.array,
  onSaveSuccess: PropTypes.func
};

PlatformCommissionsSettingsModal.defaultProps = {
  initialCommissions: null,
  onSaveSuccess: undefined
};

export default PlatformCommissionsSettingsModal;
